<template>
  <v-card class="mt-6">
    <v-card-title>
      <span class="text-h5">Publier sur WordPress</span>
    </v-card-title>
    <v-card-text>
      <v-row v-if="!hasAllRequiredContent">
        <v-col cols="12">
          <v-alert type="warning" outlined>
            <strong>Contenu manquant</strong>
            <div class="mt-2">
              Veuillez générer tous les éléments suivants avant de publier :
            </div>
            <ul class="mt-2">
              <li v-if="!hasTitle">✗ Titre</li>
              <li v-if="!hasContent">✗ Contenu</li>
              <li v-if="!hasChapo">✗ Chapô</li>
              <li v-if="!hasMetaDescription">✗ Meta description</li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col cols="12">
          <v-alert type="success" outlined dense class="mb-4">
            <strong>Prêt à publier</strong> - Tous les éléments requis sont
            générés
          </v-alert>
          <v-alert v-if="hasGeneratedImageForPublication" type="info" outlined dense class="mb-4">
            <strong>Image IA détectée</strong> - Elle sera uploadée séparément et définie comme image à la une (thumbnail) du post.
          </v-alert>
        </v-col>

        <v-col cols="12" md="4">
          <!-- <v-select
            v-model="selectedSite"
            :items="sites"
            item-text="name"
            item-value="key"
            label="Sites"
            outlined
            dense
          ></v-select> -->
          <v-autocomplete
            class="site-connector-select"
            v-model="selectedSite"
            :items="siteItems"
            :loading="isLoadingSites"
            item-text="site.name"
            item-value="site.id"
            label="Sites"
            placeholder="Sélectionnez un site"
            outlined
            dense
            hide-no-data
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar color="grey lighten-2">
                <v-icon fab color="blue-grey darken-2">{{
                  icons.mdiWordpress
                }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-text="item.site.name"></v-list-item-title>
                <v-list-item-subtitle
                  v-text="item.site_url"
                ></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="postStatus"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Statut"
            outlined
            dense
          ></v-select>
        </v-col>

        <v-col cols="12" md="4">
          <v-btn
            color="primary"
            @click="handlePublishToWordPress"
            :loading="isPublishing"
            :disabled="!selectedSite"
            block
          >
            <v-icon left>mdi-wordpress</v-icon>
            Publier sur WordPress
          </v-btn>
        </v-col>

        <v-col cols="12" v-if="publishedUrl">
          <v-alert type="success" text>
            <strong>Article publié avec succès !</strong>
            <div class="mt-2">
              <a
                :href="publishedUrl"
                target="_blank"
                class="text-decoration-none"
              >
                {{ publishedUrl }}
                <v-icon small class="ml-1">mdi-open-in-new</v-icon>
              </a>
            </div>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mdiWordpress } from "@mdi/js";

export default {
  name: "WordPressPublisher",

  data() {
    return {
      selectedSite: null,
      siteItems: [],
      isLoadingSites: false,
      postStatus: "draft",
      isPublishing: false,
      publishedUrl: null,
      statusOptions: [
        { text: "Brouillon", value: "draft" },
        { text: "Publié", value: "publish" },
        { text: "En attente", value: "pending" },
        { text: "Privé", value: "private" },
      ],
      icons: {
        mdiWordpress,
      },
    };
  },

  computed: {
    ...mapGetters("aiFactory", ["getPromptResults"]),

    sites() {
      return this.getSites;
    },

    // hasTitle() {
    //   const extrasResult = this.getPromptResults.generate_extras?.result;
    //   if (extrasResult) {
    //     try {
    //       const extras =
    //         typeof extrasResult === "string"
    //           ? JSON.parse(extrasResult)
    //           : extrasResult;
    //       return !!extras.title;
    //     } catch {
    //       return false;
    //     }
    //   }
    //   return false;
    // },
    hasTitle() {
      return !!this.getPromptResults.generate_title?.result;
    },

    hasContent() {
      return !!this.getPromptResults.generate_content?.result;
    },

    hasChapo() {
      const extrasResult = this.getPromptResults.generate_extras?.result;
      if (extrasResult) {
        try {
          const extras =
            typeof extrasResult === "string"
              ? JSON.parse(extrasResult)
              : extrasResult;
          return !!extras.excerpt;
        } catch {
          return false;
        }
      }
      return false;
    },

    hasMetaDescription() {
      const extrasResult = this.getPromptResults.generate_extras?.result;
      if (extrasResult) {
        try {
          const extras =
            typeof extrasResult === "string"
              ? JSON.parse(extrasResult)
              : extrasResult;
          return !!extras.meta_description;
        } catch {
          return false;
        }
      }
      return false;
    },

    hasAllRequiredContent() {
      return (
        this.hasTitle &&
        this.hasContent &&
        this.hasChapo &&
        this.hasMetaDescription
      );
    },

    hasGeneratedImageForPublication() {
      const imageResult = this.getPromptResults.generate_image?.result;
      return !!(imageResult && typeof imageResult === "object" && imageResult.image_b64);
    },
  },

  mounted() {
    // Set default selected site
    //this.selectedSite = this.getSelectedSite || "closer";
    this.loadAllSites();
  },

  methods: {
    ...mapActions("aiFactory", ["publishToWordPress"]),
    ...mapActions("aiFactoryTmp", ["fetchConnectors"]),

    async handlePublishToWordPress() {
      this.isPublishing = true;
      this.publishedUrl = null;

      try {
        const result = await this.publishToWordPress({
          siteId: this.selectedSite,
          status: this.postStatus,
        });

        if (result.success) {
          this.publishedUrl = result.post_url;
          this.$toast?.success("Article publié avec succès sur WordPress") ||
            console.log("Published successfully");
        } else {
          const errorMessage = result.error || "Failed to publish";
          console.error("WordPress publishing failed:", result);
          throw new Error(errorMessage);
        }
      } catch (error) {
        this.$toast?.error(
          "Erreur lors de la publication : " + error.message
        ) || console.error(error);
      } finally {
        this.isPublishing = false;
      }
    },
    async loadAllSites() {
      this.isLoadingSites = true;
      try {
        const response = await this.fetchConnectors();
        this.siteItems = response.items;
      } catch (error) {
        console.error("Erreur lors du chargement des sites:", error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur lors du chargement de la liste des sites.`,
          color: "error",
          timeout: 4000,
        });
      } finally {
        this.isLoadingSites = false;
      }
    },
  },
};
</script>
